import React, { ReactNode } from 'react';
import { Description, Field } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

interface Props {
    description?: string;
    cta?: ReactNode;
    /**
     * Hint under the row. Rendered as a HeadlessUI Description inside a
     * Field, so a HeadlessUI control in `cta` (Toggle, ListboxButton, ...)
     * announces it as its description. That is what tells a screen reader
     * user why a disabled switch is disabled.
     */
    information?: ReactNode;
    children: ReactNode;
    className?: string;
    disabled?: boolean;
    childrenClassName?: string;
    dataTestId?: string;
}

const SettingRow = ({
    description,
    cta,
    information,
    children,
    className,
    childrenClassName,
    dataTestId,
    disabled = false,
}: Props) => (
    <Field
        as="div"
        data-test-id={dataTestId}
        className={twMerge(
            'group flex flex-col items-start justify-center gap-y-2 rounded-sm px-2 py-4',
            disabled ? 'cursor-not-allowed bg-gray-50' : 'hover:bg-gray-100',
            information ? 'w-full' : 'h-13',
            className,
        )}
    >
        <div className="flex w-full justify-between">
            <div
                className={twMerge(
                    childrenClassName
                        ? childrenClassName
                        : 'flex w-full items-center gap-x-3 text-base/5 first-letter:uppercase',
                    disabled ? 'text-gray-400' : '',
                )}
            >
                {children}
            </div>

            <div className="flex items-center gap-x-3">
                {description ? (
                    <div className={disabled ? 'text-gray-400' : ''}>
                        {description}
                    </div>
                ) : null}
                {cta ? (
                    <div className={disabled ? 'text-gray-400' : ''}>{cta}</div>
                ) : null}
            </div>
        </div>

        {information ? (
            <Description
                as="p"
                className={twMerge(
                    'text-xs font-normal',
                    disabled ? 'text-gray-400' : 'text-gray-500',
                )}
            >
                {information}
            </Description>
        ) : null}
    </Field>
);

export default SettingRow;
